import dayjs from 'dayjs'
import {
  computed,
  nextTick,
  reactive,
  ref,
  type ComputedRef,
  type Ref,
} from 'vue'

import {
  normalizeCalendarEvent,
  normalizeCalendarEvents,
  type CalendarEvent,
  type CalendarEventDisplayField,
  type CalendarProps,
} from './calendar'
import {
  createDraftClassToken,
  clamp,
  hasActiveTextSelection,
  hasClassToken,
  mergeClassName,
  normalizeDateLikeValue,
  stripClassName,
  toDateValue,
  toEditDate,
} from './calendar-utils'

type CalendarEmit = (event: any, payload: any) => void

type EditableConfig = {
  drag?: boolean
  resize?: boolean
  delete?: boolean
  create?: boolean
  selectCreate?: boolean
}

type EditPanelRef = {
  popoverRef?: { value?: HTMLElement | null }
  boundaryRef?: { value?: HTMLElement | null }
} | null

type ContextMenuPanelRef = {
  menuRef?: { value?: HTMLElement | null }
} | null

type AnchorRectLike = {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
}

const resolveExposedElement = (rawRef: unknown): HTMLElement | null => {
  if (!rawRef) {
    return null
  }

  // defineExpose 的 ref 在父实例上可能是“已解包元素”或“ref 对象”。
  if (rawRef instanceof HTMLElement) {
    return rawRef
  }

  const maybeRef = rawRef as { value?: unknown }
  return maybeRef.value instanceof HTMLElement ? maybeRef.value : null
}

const createPointAnchorRect = (x: number, y: number): AnchorRectLike => ({
  left: x,
  right: x,
  top: y,
  bottom: y,
  width: 0,
  height: 0,
})

const resolveAnchorRect = (anchor?: unknown): AnchorRectLike | null => {
  if (!anchor) {
    return null
  }

  if (anchor instanceof MouseEvent) {
    const anchorElement =
      anchor.currentTarget instanceof HTMLElement
        ? anchor.currentTarget
        : anchor.target instanceof HTMLElement
          ? anchor.target
          : null

    if (anchorElement) {
      return anchorElement.getBoundingClientRect()
    }

    return createPointAnchorRect(anchor.clientX, anchor.clientY)
  }

  if (anchor instanceof HTMLElement) {
    return anchor.getBoundingClientRect()
  }

  const maybeRect = anchor as Partial<AnchorRectLike>
  if (
    typeof maybeRect.left === 'number' &&
    typeof maybeRect.top === 'number' &&
    typeof maybeRect.right === 'number' &&
    typeof maybeRect.bottom === 'number' &&
    typeof maybeRect.width === 'number' &&
    typeof maybeRect.height === 'number'
  ) {
    return {
      left: maybeRect.left,
      top: maybeRect.top,
      right: maybeRect.right,
      bottom: maybeRect.bottom,
      width: maybeRect.width,
      height: maybeRect.height,
    }
  }

  const maybePoint = anchor as { x?: unknown; y?: unknown }
  if (typeof maybePoint.x === 'number' && typeof maybePoint.y === 'number') {
    return createPointAnchorRect(maybePoint.x, maybePoint.y)
  }

  return null
}

export interface UseCalendarEditorOptions {
  props: Pick<
    CalendarProps,
    'readonly' | 'eventTitleKey' | 'selectable' | 'disableCreateInPast'
  >
  emit: CalendarEmit
  defaultEventTitle: string
  slotStepMinutes: Ref<number>
  editableDisplayFields: ComputedRef<CalendarEventDisplayField[]>
  internalEvents: Ref<CalendarEvent[]>
  currentEditableEvent: ComputedRef<EditableConfig>
  canQuickCreate: ComputedRef<boolean>
  canSelectCreate: ComputedRef<boolean>
  getEventIdentifier: (event?: Partial<CalendarEvent>) => string
  commitEvents: (events: CalendarEvent[]) => void
  patchEvent: (
    nextEvent: CalendarEvent,
    previousEvent?: CalendarEvent,
  ) => CalendarEvent[]
  canCreateAtRange: (
    startValue: string | Date | undefined,
    endValue?: string | Date,
  ) => boolean
  calendarContainerRef?: Ref<HTMLElement | null | undefined>
}

export const useCalendarEditor = (options: UseCalendarEditorOptions) => {
  const {
    props,
    emit,
    defaultEventTitle,
    slotStepMinutes,
    editableDisplayFields,
    internalEvents,
    currentEditableEvent,
    canQuickCreate,
    canSelectCreate,
    getEventIdentifier,
    commitEvents,
    patchEvent,
    canCreateAtRange,
    calendarContainerRef,
  } = options

  const editPanelRef = ref<EditPanelRef>(null)

  const editBoundaryStyle = ref<Record<string, string>>({
    position: 'fixed',
    left: '-9999px',
    top: '-9999px',
    width: '0px',
    height: '0px',
    visibility: 'hidden',
  })

  const syncEditBoundaryStyle = () => {
    const containerEl = calendarContainerRef?.value
    if (!(containerEl instanceof HTMLElement)) {
      editBoundaryStyle.value = {
        ...editBoundaryStyle.value,
        left: '-9999px',
        top: '-9999px',
        width: '0px',
        height: '0px',
        visibility: 'hidden',
      }
      return
    }

    const rect = containerEl.getBoundingClientRect()
    editBoundaryStyle.value = {
      ...editBoundaryStyle.value,
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${Math.max(rect.width, 0)}px`,
      height: `${Math.max(rect.height, 0)}px`,
      visibility: 'hidden',
    }
  }

  const resolveBoundaryProxyRect = (): AnchorRectLike => {
    const boundaryEl = resolveExposedElement(editPanelRef.value?.boundaryRef)
    if (boundaryEl instanceof HTMLElement) {
      const boundaryRect = boundaryEl.getBoundingClientRect()
      if (boundaryRect.width > 0 && boundaryRect.height > 0) {
        return boundaryRect
      }
    }

    return {
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      width: window.innerWidth,
      height: window.innerHeight,
    }
  }

  const editPopoverOpen = ref(false)
  const editPresentationMode = ref<'popover' | 'drawer'>('popover')
  const editPopoverPlacement = ref<'right' | 'left' | 'top' | 'bottom'>('right')
  const editingEventIdentifier = ref('')
  const editingDraftClassToken = ref('')
  const newDraftEventIdRef = ref('')
  const createDraftEventRef = ref<CalendarEvent | null>(null)
  const contextMenuPanelRef = ref<ContextMenuPanelRef>(null)
  const contextMenuOpen = ref(false)
  const contextMenuEvent = ref<CalendarEvent | null>(null)

  const contextMenuStyle = ref<Record<string, string>>({
    position: 'fixed',
    left: '-9999px',
    top: '-9999px',
    visibility: 'hidden',
  })
  const editPopoverStyle = ref<Record<string, string>>({
    position: 'absolute',
    left: '-9999px',
    top: '-9999px',
    visibility: 'hidden',
  })

  const editFormData = reactive<Record<string, any>>({
    title: '',
    timeRange: [],
  })

  const isCreatingEvent = computed(() => Boolean(createDraftEventRef.value))
  const editingEventTitle = computed(
    () => String(editFormData.title || '').trim() || defaultEventTitle,
  )

  const isEditingTargetEvent = (
    event: CalendarEvent,
    identifier = editingEventIdentifier.value,
    draftToken = editingDraftClassToken.value,
  ) => {
    const matchedByIdentifier =
      Boolean(identifier) && getEventIdentifier(event) === identifier
    const matchedByDraftToken =
      Boolean(draftToken) && hasClassToken(event.class, draftToken)

    return matchedByIdentifier || matchedByDraftToken
  }

  const resetEditFormData = () => {
    Object.keys(editFormData).forEach((key) => {
      delete editFormData[key]
    })
    editFormData.title = ''
    editFormData.timeRange = []
  }

  const mergeEditFormData = (source?: Partial<CalendarEvent>) => {
    resetEditFormData()

    if (!source || typeof source !== 'object') {
      return
    }

    Object.keys(source).forEach((key) => {
      editFormData[key] = source[key as keyof CalendarEvent]
    })
  }

  const applyEventTitle = (event: CalendarEvent, nextTitle?: string) => {
    const sourceTitle =
      nextTitle ??
      String(
        event.title ||
          (props.eventTitleKey ? event[props.eventTitleKey] : '') ||
          '',
      ).trim()
    const normalizedTitle = sourceTitle || defaultEventTitle

    event.title = normalizedTitle
    if (props.eventTitleKey && props.eventTitleKey !== 'title') {
      event[props.eventTitleKey] = normalizedTitle
    }

    return normalizedTitle
  }

  const closeContextMenu = () => {
    contextMenuOpen.value = false
    contextMenuEvent.value = null
  }

  const openContextMenu = async (
    event: CalendarEvent,
    nativeEvent: MouseEvent,
  ) => {
    syncEditBoundaryStyle()

    contextMenuEvent.value = event
    contextMenuStyle.value = {
      ...contextMenuStyle.value,
      left: '-9999px',
      top: '-9999px',
      visibility: 'hidden',
    }
    contextMenuOpen.value = true

    await nextTick()

    const menuEl = resolveExposedElement(contextMenuPanelRef.value?.menuRef)
    const boundaryRect = resolveBoundaryProxyRect()
    const margin = 12
    const menuWidth = menuEl?.offsetWidth || 180
    const menuHeight = menuEl?.offsetHeight || 88
    // 以 boundary-proxy 的 viewport 坐标范围做硬裁切，四边不超出代理区域
    const nextLeft = clamp(
      nativeEvent.clientX,
      boundaryRect.left + margin,
      boundaryRect.right - menuWidth - margin,
    )
    const nextTop = clamp(
      nativeEvent.clientY,
      boundaryRect.top + margin,
      boundaryRect.bottom - menuHeight - margin,
    )

    contextMenuStyle.value = {
      ...contextMenuStyle.value,
      left: `${nextLeft}px`,
      top: `${nextTop}px`,
      visibility: 'visible',
    }
  }

  const openEditPopover = async (
    event: CalendarEvent,
    draftClassToken = '',
    anchor?: unknown,
  ) => {
    if (props.readonly) {
      return
    }

    closeContextMenu()
    // 统一约定：openEditPopover 始终优先展示弹窗，抽屉仅由 openCreateEventDrawer 触发。
    editPresentationMode.value = 'popover'

    const start = toDateValue(event.start) || new Date()
    const end =
      toDateValue(event.end) ||
      new Date(
        start.getTime() + Math.max(slotStepMinutes.value, 30) * 60 * 1000,
      )

    mergeEditFormData(event)
    editFormData.title =
      String(
        event.title || (props.eventTitleKey ? event[props.eventTitleKey] : ''),
      ).trim() || defaultEventTitle
    editFormData.timeRange = [dayjs(start), dayjs(end)]

    editableDisplayFields.value.forEach((field) => {
      editFormData[field.key] = event[field.key]
    })

    editingEventIdentifier.value = getEventIdentifier(event)
    editingDraftClassToken.value = draftClassToken

    editPopoverStyle.value = {
      ...editPopoverStyle.value,
      position: 'absolute',
      left: '-9999px',
      top: '-9999px',
      visibility: 'hidden',
    }
    syncEditBoundaryStyle()

    editPopoverOpen.value = true

    await nextTick()
    let popupEl = resolveExposedElement(editPanelRef.value?.popoverRef)
    if (!popupEl && editPresentationMode.value === 'popover') {
      // 子组件拆分后 Teleport 挂载可能晚一个 tick，补一次读取。
      await nextTick()
      popupEl = resolveExposedElement(editPanelRef.value?.popoverRef)
    }

    if (!popupEl) {
      return
    }

    const margin = 12
    const gap = 10

    // 将定位逻辑提取为内部函数，便于初次定位与延迟二次定位复用
    const applyPosition = (resolvedAnchor: unknown) => {
      const currentPopupEl = resolveExposedElement(editPanelRef.value?.popoverRef)
      if (!currentPopupEl) {
        return
      }
      const currentPopupRect = {
        width: currentPopupEl.offsetWidth,
        height: currentPopupEl.offsetHeight,
      }
      const currentBoundaryRect = resolveBoundaryProxyRect()

      const clampLeft = (v: number) =>
        clamp(
          v,
          margin,
          currentBoundaryRect.width - currentPopupRect.width - margin,
        )
      const clampTop = (v: number) =>
        clamp(
          v,
          margin,
          currentBoundaryRect.height - currentPopupRect.height - margin,
        )

      const anchorRect = resolveAnchorRect(resolvedAnchor)
      const localAnchorRect = anchorRect
        ? {
            left: anchorRect.left - currentBoundaryRect.left,
            right: anchorRect.right - currentBoundaryRect.left,
            top: anchorRect.top - currentBoundaryRect.top,
            bottom: anchorRect.bottom - currentBoundaryRect.top,
            width: anchorRect.width,
            height: anchorRect.height,
          }
        : null

      // 默认居中
      let nextLeft = clampLeft(
        (currentBoundaryRect.width - currentPopupRect.width) / 2,
      )
      let nextTop = clampTop(
        (currentBoundaryRect.height - currentPopupRect.height) / 2,
      )
      let nextPlacement: 'right' | 'left' | 'top' | 'bottom' = 'top'

      if (localAnchorRect) {
        const preferRight =
          currentBoundaryRect.width - localAnchorRect.right >=
          localAnchorRect.left
        const preferBottom =
          currentBoundaryRect.height - localAnchorRect.bottom >=
          localAnchorRect.top

        const placements: Array<'right' | 'left' | 'bottom' | 'top'> = [
          preferRight ? 'right' : 'left',
          preferRight ? 'left' : 'right',
          preferBottom ? 'bottom' : 'top',
          preferBottom ? 'top' : 'bottom',
        ]

        const candidates = placements.map((placement) => {
          let rawLeft = 0
          let rawTop = 0

          if (placement === 'right') {
            rawLeft = localAnchorRect.right + gap
            rawTop =
              localAnchorRect.top +
              (localAnchorRect.height - currentPopupRect.height) / 2
          } else if (placement === 'left') {
            rawLeft = localAnchorRect.left - currentPopupRect.width - gap
            rawTop =
              localAnchorRect.top +
              (localAnchorRect.height - currentPopupRect.height) / 2
          } else if (placement === 'bottom') {
            rawLeft =
              localAnchorRect.left +
              (localAnchorRect.width - currentPopupRect.width) / 2
            rawTop = localAnchorRect.bottom + gap
          } else {
            rawLeft =
              localAnchorRect.left +
              (localAnchorRect.width - currentPopupRect.width) / 2
            rawTop = localAnchorRect.top - currentPopupRect.height - gap
          }

          const overflow =
            Math.max(0, margin - rawLeft) +
            Math.max(
              0,
              rawLeft +
                currentPopupRect.width -
                (currentBoundaryRect.width - margin),
            ) +
            Math.max(0, margin - rawTop) +
            Math.max(
              0,
              rawTop +
                currentPopupRect.height -
                (currentBoundaryRect.height - margin),
            )

          return {
            placement,
            left: clampLeft(rawLeft),
            top: clampTop(rawTop),
            overflow,
          }
        })

        const bestCandidate =
          candidates.find((c) => c.overflow === 0) ||
          candidates.reduce((best, c) =>
            c.overflow < best.overflow ? c : best,
          )

        nextLeft = bestCandidate.left
        nextTop = bestCandidate.top
        nextPlacement = bestCandidate.placement
      }

      editPopoverPlacement.value = nextPlacement
      editPopoverStyle.value = {
        ...editPopoverStyle.value,
        position: 'absolute',
        left: `${nextLeft}px`,
        top: `${nextTop}px`,
        visibility: 'visible',
      }
    }

    // 优先使用外部传入的 anchor，否则尝试立即查找草稿日程 DOM 元素。
    // 月视图：自定义 #cell 模板通过 newDraftEventIdRef 给外层 div 加 is-new class
    // 周/日视图：VueCal 将 event.class（含 draftClassToken）应用到 .vuecal__event，通过 token class 查找
    const findDraftAnchorEl = () => {
      const container = calendarContainerRef?.value
      if (!container || !draftClassToken) return undefined
      return (
        container.querySelector('.is-new') ??
        container.querySelector(`.${draftClassToken}`) ??
        undefined
      )
    }

    const immediateAnchor: unknown = anchor || findDraftAnchorEl()
    if (immediateAnchor || !draftClassToken) {
      // 有明确锚点或非草稿创建场景，立即定位并显示
      applyPosition(immediateAnchor)
    } else if (calendarContainerRef?.value) {
      // 草稿日程尚未渲染到 DOM（月视图 vuecal 延迟），保持隐藏
      // 500ms 后再次查找锚点（月视图用 .is-new，周/日视图用 draftClassToken）
      const container = calendarContainerRef.value
      const snapshotToken = draftClassToken
      setTimeout(() => {
        if (
          !editPopoverOpen.value ||
          editingDraftClassToken.value !== snapshotToken
        ) {
          return
        }
        const draftEl =
          container.querySelector('.is-new') ??
          container.querySelector(`.${snapshotToken}`) ??
          undefined
        applyPosition(draftEl)
      }, 500)
    }
  }

  const clearEditingDraftToken = () => {
    const token = editingDraftClassToken.value
    const identifier = editingEventIdentifier.value
    if (!token || !identifier) {
      return
    }

    const nextEvents = internalEvents.value.map((event) => {
      if (getEventIdentifier(event) !== identifier) {
        return event
      }

      return {
        ...event,
        class: stripClassName(event.class, token),
      }
    })

    commitEvents(nextEvents)
    editingDraftClassToken.value = ''
  }

  const closeEditPopover = () => {
    editingEventIdentifier.value = ''
    editingDraftClassToken.value = ''
    newDraftEventIdRef.value = ''
    createDraftEventRef.value = null
    closeContextMenu()
    editPopoverOpen.value = false
  }

  const openCreateEventDrawer = (baseEvent?: Partial<CalendarEvent>) => {
    if (props.readonly) {
      return
    }

    if (!canQuickCreate.value) {
      return
    }

    const now = new Date()
    const start = toDateValue(baseEvent?.start) || now
    const end =
      toDateValue(baseEvent?.end) ||
      new Date(
        start.getTime() + Math.max(slotStepMinutes.value, 30) * 60 * 1000,
      )

    if (!canCreateAtRange(start, end)) {
      return
    }

    mergeEditFormData(baseEvent)
    editFormData.title =
      String(baseEvent?.title || baseEvent?.[props.eventTitleKey] || '').trim() ||
      defaultEventTitle
    editFormData.timeRange = [dayjs(start), dayjs(end)]

    editableDisplayFields.value.forEach((field) => {
      editFormData[field.key] = baseEvent?.[field.key]
    })

    createDraftEventRef.value = {
      ...(baseEvent as CalendarEvent),
      start,
      end,
      split: baseEvent?.split,
      class: (baseEvent as CalendarEvent)?.class,
    }

    editingEventIdentifier.value = ''
    editingDraftClassToken.value = ''
    newDraftEventIdRef.value = String(createDraftEventRef.value?.id ?? '')
    editPresentationMode.value = 'drawer'
    editPopoverOpen.value = true
  }

  const handleSwitchPresentation = () => {
    editPresentationMode.value =
      editPresentationMode.value === 'popover' ? 'drawer' : 'popover'

    if (editPresentationMode.value === 'popover') {
      const currentEvent = internalEvents.value.find((event) =>
        isEditingTargetEvent(event),
      )

      if (currentEvent) {
        openEditPopover(currentEvent, editingDraftClassToken.value)
      }
    }
  }

  const removeEventWithMatcher = (matcher: (event: CalendarEvent) => boolean) => {
    if (props.readonly) {
      return false
    }

    if (currentEditableEvent.value.delete === false) {
      return false
    }

    let deletedEvent: CalendarEvent | undefined
    const nextEvents = internalEvents.value.filter((event) => {
      const matched = matcher(event)
      if (matched && !deletedEvent) {
        deletedEvent = normalizeCalendarEvent(event)
      }

      return !matched
    })

    if (!deletedEvent) {
      return false
    }

    commitEvents(nextEvents)
    emit('event-delete', {
      raw: {
        event: deletedEvent,
      },
      event: deletedEvent,
      events: normalizeCalendarEvents(nextEvents),
    })

    return true
  }

  const deleteEvent = (target?: CalendarEvent) => {
    if (props.readonly) {
      return false
    }

    if (!target) {
      return false
    }

    const identifier = getEventIdentifier(target)
    if (!identifier) {
      return false
    }

    return removeEventWithMatcher(
      (event) => getEventIdentifier(event) === identifier,
    )
  }

  const copyEvent = (target?: CalendarEvent) => {
    if (props.readonly) {
      return false
    }

    if (!target) {
      return false
    }

    const identifier = getEventIdentifier(target)
    const sourceEvent =
      internalEvents.value.find(
        (event) => getEventIdentifier(event) === identifier,
      ) || target

    const sourceStart = toDateValue(sourceEvent.start)
    if (!sourceStart || Number.isNaN(sourceStart.getTime())) {
      return false
    }

    const sourceEnd =
      toDateValue(sourceEvent.end) ||
      new Date(
        sourceStart.getTime() + Math.max(slotStepMinutes.value, 30) * 60 * 1000,
      )
    const shiftMs = 60 * 60 * 1000

    const clonedEvent: CalendarEvent = {
      ...sourceEvent,
      id: `event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      _eid: undefined,
      class: stripClassName(sourceEvent.class, editingDraftClassToken.value),
      start: new Date(sourceStart.getTime() + shiftMs),
      end: new Date(sourceEnd.getTime() + shiftMs),
    }

    const nextEvents = [...internalEvents.value, clonedEvent]
    const normalizedClonedEvent = normalizeCalendarEvent(clonedEvent)
    commitEvents(nextEvents)
    emit('event-edit', {
      raw: {
        event: normalizedClonedEvent,
        originalEvent: normalizeCalendarEvent(sourceEvent),
        isCreate: true,
      },
      event: normalizedClonedEvent,
      events: normalizeCalendarEvents(nextEvents),
    })

    return true
  }

  const handleDeleteEditEvent = () => {
    if (props.readonly) {
      return
    }

    const removed = removeEventWithMatcher((event) => isEditingTargetEvent(event))
    if (!removed) {
      closeEditPopover()
      return
    }

    closeEditPopover()
  }

  const handleMaskClose = () => {
    if (hasActiveTextSelection()) {
      window.getSelection?.()?.removeAllRanges()
      return
    }

    handleConfirmEdit()
  }

  const handleConfirmEdit = () => {
    if (props.readonly) {
      closeEditPopover()
      return
    }

    if (!editPopoverOpen.value) {
      return
    }

    const identifier = editingEventIdentifier.value
    const draftToken = editingDraftClassToken.value

    const [rangeStart, rangeEnd] = Array.isArray(editFormData.timeRange)
      ? editFormData.timeRange
      : []

    const nextStart = toEditDate(rangeStart)
    const nextEnd = toEditDate(rangeEnd)
    const nextTitle = String(editFormData.title || '').trim() || defaultEventTitle

    if (createDraftEventRef.value) {
      const baseEvent = createDraftEventRef.value
      const createdEvent: CalendarEvent = {
        ...baseEvent,
        id:
          baseEvent.id ??
          `event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: nextTitle,
        start: nextStart || toDateValue(baseEvent.start) || new Date(),
        end:
          nextEnd ||
          toDateValue(baseEvent.end) ||
          new Date(
            (nextStart || toDateValue(baseEvent.start) || new Date()).getTime() +
              Math.max(slotStepMinutes.value, 30) * 60 * 1000,
          ),
      }

      if (!canCreateAtRange(createdEvent.start, createdEvent.end)) {
        return
      }

      applyEventTitle(createdEvent, nextTitle)
      editableDisplayFields.value.forEach((field) => {
        createdEvent[field.key] = editFormData[field.key]
      })

      const nextEvents = [...internalEvents.value, createdEvent]
      const normalizedCreatedEvent = normalizeCalendarEvent(
        nextEvents.find((e) => getEventIdentifier(e) === getEventIdentifier(createdEvent)) ??
          createdEvent,
      )
      commitEvents(nextEvents)
      emit('event-edit', {
        raw: {
          event: normalizedCreatedEvent,
          isCreate: true,
        },
        event: normalizedCreatedEvent,
        events: normalizeCalendarEvents(nextEvents),
      })
      closeEditPopover()
      return
    }

    if (!identifier && !draftToken) {
      closeEditPopover()
      return
    }

    let hasMatchedTarget = false
    let originalTargetEvent: CalendarEvent | undefined
    let editedTargetEvent: CalendarEvent | undefined
    const nextEvents = internalEvents.value.map((event) => {
      if (!isEditingTargetEvent(event, identifier, draftToken)) {
        return event
      }
      hasMatchedTarget = true
      if (!originalTargetEvent) {
        originalTargetEvent = normalizeCalendarEvent(event)
      }

      const nextEvent: CalendarEvent = {
        ...event,
        title: nextTitle,
        class: stripClassName(event.class, editingDraftClassToken.value),
      }
      applyEventTitle(nextEvent, nextTitle)

      if (nextStart) {
        nextEvent.start = nextStart
      }
      if (nextEnd) {
        nextEvent.end = nextEnd
      }

      editableDisplayFields.value.forEach((field) => {
        nextEvent[field.key] = editFormData[field.key]
      })

      if (!editedTargetEvent) {
        editedTargetEvent = normalizeCalendarEvent(nextEvent)
      }

      return nextEvent
    })

    if (!hasMatchedTarget) {
      closeEditPopover()
      return
    }

    commitEvents(nextEvents)
    if (editedTargetEvent) {
      emit('event-edit', {
        raw: {
          event: editedTargetEvent,
          originalEvent: originalTargetEvent,
          isCreate: false,
        },
        event: editedTargetEvent,
        events: normalizeCalendarEvents(nextEvents),
      })
    }
    closeEditPopover()
  }

  const handleConfirmAndAdd = () => {
    if (!isCreatingEvent.value) {
      handleConfirmEdit()
      return
    }

    const [rangeStart, rangeEnd] = Array.isArray(editFormData.timeRange)
      ? editFormData.timeRange
      : []
    const nextStart = toEditDate(rangeStart)
    const nextEnd = toEditDate(rangeEnd)
    const split = createDraftEventRef.value?.split

    handleConfirmEdit()

    openCreateEventDrawer({
      start: nextStart || undefined,
      end: nextEnd || undefined,
      split,
    })
  }

  const createDraftEventAndOpenEditor = (
    baseEvent: CalendarEvent,
    fallbackSplit?: string | number,
  ) => {
    if (props.readonly) {
      return
    }

    if (!props.selectable) {
      return
    }

    const start = toDateValue(baseEvent.start) || new Date()
    const end =
      toDateValue(baseEvent.end) ||
      new Date(
        start.getTime() + Math.max(slotStepMinutes.value, 30) * 60 * 1000,
      )

    const isMonthViewEvent =
      start.getHours() === 0 &&
      start.getMinutes() === 0 &&
      start.getSeconds() === 0 &&
      end.getHours() === 0 &&
      end.getMinutes() === 0 &&
      end.getSeconds() === 0

    if (props.disableCreateInPast) {
      if (isMonthViewEvent) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        if (start.getTime() < today.getTime()) {
          return
        }
      } else if (!canCreateAtRange(start, end)) {
        return
      }
    }

    const draftClassToken = createDraftClassToken()
    const baseTitle = applyEventTitle(baseEvent)
    // 剥离 VueCal 内部字段（_, style, startMinutes, endMinutes 等），
    // 避免拖拽创建时 VueCal 临时状态污染 draftEvent 及对外 select payload
    const cleanBaseEvent = { ...baseEvent } as CalendarEvent
    delete (cleanBaseEvent as any)._
    delete (cleanBaseEvent as any).style
    delete (cleanBaseEvent as any).startMinutes
    delete (cleanBaseEvent as any).endMinutes
    const draftEvent: CalendarEvent = {
      ...cleanBaseEvent,
      id:
        cleanBaseEvent.id ??
        `event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: baseTitle,
      start,
      end,
      split: cleanBaseEvent.split ?? fallbackSplit,
      class: mergeClassName(cleanBaseEvent.class, draftClassToken),
    }
    applyEventTitle(draftEvent, baseTitle)
    newDraftEventIdRef.value = String(draftEvent.id ?? '')

    if (editPopoverOpen.value) {
      clearEditingDraftToken()
      closeEditPopover()
    }

    const nextEvents = patchEvent(draftEvent, baseEvent)

    commitEvents(nextEvents)

    emit('select', {
      raw: {
        start,
        end,
        split: draftEvent.split,
      },
      // 对外暴露的事件数据中去掉内部实现类（draftClassToken），避免污染外部消费者数据
      events: normalizeCalendarEvents(
        nextEvents.map((e) => ({
          ...e,
          class: stripClassName(e.class, draftClassToken),
        })),
      ),
    })

    openEditPopover(draftEvent, draftClassToken)
  }

  const resolveCreatedEventFromPayload = (payload: any) => {
    const candidate =
      payload?.event && typeof payload.event === 'object'
        ? payload.event
        : payload

    if (!candidate || typeof candidate !== 'object') {
      return undefined
    }

    return candidate as CalendarEvent
  }

  const removeCreatedDraftEvent = (event?: CalendarEvent) => {
    if (!event) {
      return
    }

    if (typeof event.delete === 'function') {
      event.delete(3)
    }

    const removeTask = () => {
      const targetIdentifier = getEventIdentifier(event)
      const targetStart = toDateValue(event.start)?.getTime()
      const targetEnd = toDateValue(event.end)?.getTime()
      let removed = false

      const nextEvents = internalEvents.value.filter((item) => {
        const matchedByIdentifier =
          Boolean(targetIdentifier) &&
          getEventIdentifier(item) === targetIdentifier

        const itemStart = toDateValue(item.start)?.getTime()
        const itemEnd = toDateValue(item.end)?.getTime()
        const matchedByExactTime =
          normalizeDateLikeValue(item.start) ===
            normalizeDateLikeValue(event.start) &&
          normalizeDateLikeValue(item.end) === normalizeDateLikeValue(event.end)

        const matchedByNearTime =
          targetStart !== undefined &&
          itemStart !== undefined &&
          Math.abs(itemStart - targetStart) <= 60 * 1000 &&
          ((targetEnd !== undefined &&
            itemEnd !== undefined &&
            Math.abs(itemEnd - targetEnd) <= 60 * 1000) ||
            (targetEnd === undefined && itemEnd === undefined))

        const matched =
          matchedByIdentifier || matchedByExactTime || matchedByNearTime

        if (matched) {
          removed = true
        }

        return !matched
      })

      if (removed) {
        commitEvents(nextEvents)
      }
    }

    removeTask()
    void nextTick(() => {
      requestAnimationFrame(() => {
        removeTask()
      })
    })
  }

  const handleEventDragCreate = (payload: any) => {
    if (props.readonly) {
      return
    }

    const event = resolveCreatedEventFromPayload(payload)
    if (!event) {
      return
    }

    if (!canSelectCreate.value) {
      removeCreatedDraftEvent(event)
      return
    }

    if (!canCreateAtRange(event.start, event.end)) {
      removeCreatedDraftEvent(event)
      return
    }

    createDraftEventAndOpenEditor(event, event.split)
  }

  return {
    editPanelRef,
    editPopoverOpen,
    editPresentationMode,
    editPopoverPlacement,
    createDraftEventRef,
    contextMenuPanelRef,
    contextMenuOpen,
    contextMenuEvent,
    contextMenuStyle,
    editPopoverStyle,
    editBoundaryStyle,
    editFormData,
    isCreatingEvent,
    editingEventTitle,
    applyEventTitle,
    closeContextMenu,
    openContextMenu,
    openEditPopover,
    newDraftEventIdRef,
    clearEditingDraftToken,
    closeEditPopover,
    openCreateEventDrawer,
    handleSwitchPresentation,
    deleteEvent,
    copyEvent,
    handleDeleteEditEvent,
    handleMaskClose,
    handleConfirmEdit,
    handleConfirmAndAdd,
    createDraftEventAndOpenEditor,
    resolveCreatedEventFromPayload,
    removeCreatedDraftEvent,
    handleEventDragCreate,
  }
}
